/**
 * The cave run — a state machine over the server's authoritative run.
 *
 *   idle → walking → blocked (axe too low)
 *                  → breaking → loot → adPending → walking
 *
 * The client predicts crack stages and plays FX immediately, then reconciles to
 * whatever the server returns. If the two disagree, the server wins silently —
 * a mismatch is not worth showing the player.
 *
 * What this store deliberately does NOT do: decide loot, decide crits, decide
 * whether a hit landed, or add up currency. All of that is server-side.
 */
import { create } from 'zustand';

import { ApiError } from '@/api/client';
import {
  cave,
  type ClaimDTO, type HitDTO, type LootDTO, type RockDTO, type RunDTO,
  type RunSummaryDTO, type SearchEventDTO, type ZoneDTO,
} from '@/api/endpoints';
import { LOOT_CARD_DELAY_MS, placementOf } from '@/game/engine/field';
import type { RockSlot } from '@/game/engine/projection';
import { useSession } from './session';

export type RunPhase =
  | 'idle'        // at the surface
  | 'walking'     // in the cave, nothing in the way
  | 'blocked'     // a rock the current axe cannot break
  | 'breaking'    // mid-swing on a breakable rock
  | 'loot'        // broken, loot card open
  | 'adPending'   // waiting on a rewarded ad to verify
  | 'stepping';   // walk animation in flight

export interface Blocker {
  code: 'LAMP_LEVEL_TOO_LOW' | 'TORCH_EMPTY' | 'NOT_ENOUGH_ENERGY' | 'CHEAT_SUSPECTED'
      | 'AXE_LEVEL_TOO_LOW' | 'NOT_ENOUGH_COINS' | 'NOT_ENOUGH_KYAT';
  message: string;
  details: Record<string, unknown>;
}

/**
 * A rock that has just been knocked out of the wall and is falling.
 *
 * Purely what the animation needs, and deliberately NOT a `RockDTO`: this rock no longer
 * exists as far as the server is concerned — it has been broken, its loot is decided, and
 * it is out of `visible_spawns`. Keeping the DTO around would leave a payout-shaped object
 * in the store after its payout was settled, and the next thing to read `spawns` would
 * offer a tap that 409s.
 *
 * The depth is absolute (`depthM`), not metres-ahead, so the layer derives the distance
 * from the player's own depth exactly the way `layoutField` does. It cannot go stale.
 */
export interface KnockOut {
  uuid: string;
  slot: RockSlot;
  u: number;
  v: number;
  depthM: number;
  materialKey: string;
  /** `performance.now()` at the breaking swing. The arc is a pure function of elapsed time. */
  startedAt: number;
}

/**
 * A rock that has finished falling and is now part of the floor.
 *
 * The cave should show what happened in it. Before this, a broken rock fell for 0.9 s and
 * then blinked out, which put the vanishing back — just delayed by a second. Now it stays
 * where it landed, and walking back up the tunnel means walking past your own rubble.
 *
 * `u`/`v` are where the arc actually ended, not where the rock started: they come from the
 * layer that ran the physics, so the rock does not move on the frame it stops being animated.
 */
export interface SettledRock {
  uuid: string;
  u: number;
  v: number;
  depthM: number;
  materialKey: string;
  /** The angle the physics arc actually landed at — see `settleDebris`. */
  spin: number;
}

/**
 * How many pieces of settled rubble a run remembers.
 *
 * Not unbounded: a long run breaks hundreds of rocks, and every one of them would be a DOM
 * node re-projected on every step for the rest of the descent. Eight is enough for the floor
 * behind you to look worked-over — the lamp only reaches a handful of metres anyway, so the
 * cap is almost never what removes a piece from view.
 */
export const SETTLED_MAX = 8;

/** The one cave nobody can be locked out of. */
export const STARTER_CAVE = 'abandoned_mine';

const CAVE_KEY_STORAGE = 'shwetap.cave';
const STARTER_CAVE_NAME = 'Abandoned Mine';

/**
 * The chosen cave, off disk.
 *
 * Deliberately not part of `persist.ts`'s snapshot: that cache is a copy of *server*
 * state and expires after a day, and a preference that quietly resets to the starter
 * cave after 24 hours would look like the game forgetting a map the player earned.
 * Every failure is "the starter cave", never a throw — a private window with storage
 * blocked must still be able to dig.
 *
 * The name is stored beside the key so the menu can say *Glass Reef* on a cold start
 * without a request. It is a label only: nothing branches on it, and if it is stale
 * the first `/cave/enter` or `/cave/maps` replaces it.
 */
function readCave(): { key: string; name: string } {
  try {
    const raw = localStorage.getItem(CAVE_KEY_STORAGE);
    if (!raw) return { key: STARTER_CAVE, name: STARTER_CAVE_NAME };
    const parsed = JSON.parse(raw) as { key?: string; name?: string };
    return {
      key: parsed.key || STARTER_CAVE,
      name: parsed.name || STARTER_CAVE_NAME,
    };
  } catch {
    return { key: STARTER_CAVE, name: STARTER_CAVE_NAME };
  }
}

function writeCave(key: string, name: string): void {
  try {
    localStorage.setItem(CAVE_KEY_STORAGE, JSON.stringify({ key, name }));
  } catch {
    /* Storage full or blocked. The choice still holds for this session. */
  }
}

/**
 * The client's own step floor, kept just above the server's 400 ms
 * (core/anticheat.py STEP_MIN_INTERVAL_MS).
 *
 * Without this a player tapping MOVE FORWARD at a normal speed sends steps the
 * server is certain to reject: measured 4–5 CHEAT_SUSPECTED out of 8 taps at
 * 250 ms. Refusing locally costs nothing and keeps `risk_score` clean — the
 * server bumps it on every rejection, so client spam looks like cheating.
 */
export const STEP_MIN_MS = 440;

/**
 * True while a `/cave/hit` is on the wire. Deliberately module-level rather than
 * store state: it is a transport detail, and putting it in the store would
 * re-render the whole cave twice per tap for something nothing renders.
 */
let swingInFlight = false;

/**
 * The pending loot-card reveal, held while the broken rock is still falling.
 *
 * Module-level for the same reason as the swing guard: it is a timer handle, not state,
 * and nothing renders it.
 */
let lootTimer: ReturnType<typeof setTimeout> | null = null;

/** Cancel a held reveal. Any path that ends or resets a run has to call this. */
function cancelLootTimer(): void {
  if (lootTimer !== null) {
    clearTimeout(lootTimer);
    lootTimer = null;
  }
}

/** Test-only: the guard outlives a single store, so tests must be able to clear it. */
export function __resetSwingGuard(): void {
  swingInFlight = false;
  cancelLootTimer();
}

interface RunState {
  phase: RunPhase;
  runId: string | null;
  depth: number;
  torchFuel: number;
  torchFuelMax: number;
  zone: ZoneDTO;
  /**
   * Which cave the next descent goes into.
   *
   * Held here and mirrored to `localStorage` so tapping DIG returns you to the cave
   * you chose rather than the starter one — a player who assembled a map should not
   * have to re-pick it every run. It is a *preference*, never permission: the server
   * re-checks `cave_maps` on every `enter()` and refuses a key the player has not
   * earned with `CAVE_LOCKED`.
   */
  caveKey: string;
  /** Display name of `caveKey`. A label — nothing branches on it. */
  caveName: string;
  /**
   * What the last wall search turned up, until the player dismisses it.
   *
   * `{type:'none'}` results are kept rather than dropped: "Solid rock. Nothing here."
   * is the answer to a question the player asked and paid fuel for, and silently
   * showing nothing would read as the button being broken.
   */
  searchEvent: SearchEventDTO | null;
  /** True while a `/cave/search` is on the wire, so the two buttons can go quiet. */
  searching: boolean;
  /**
   * What the last finished descent came to, until the player closes the card.
   *
   * `/cave/exit` has returned this since the run engine was written and the store threw
   * it away, so a run ended by unmounting the cave and landing on the menu with no
   * account of what just happened — no depth, no haul, no time. The one moment a
   * session has a natural ending was the one moment the game said nothing.
   */
  summary: RunSummaryDTO | null;
  rock: RockDTO | null;
  /**
   * Every rock the lamp reaches, nearest first — the corridor's stone, not just the
   * one in the way. Server-owned: the client never invents a rock, because a rock is
   * a payout (ARCHITECTURE.md §1). `rock` above remains the *featured* path rock, the
   * one the axe gate is about and the only one that can be treasure.
   */
  spawns: RockDTO[];
  /**
   * Rocks mid-fall. Empty almost always — at most one, for `KNOCKOUT_SECONDS`.
   *
   * Separate from `spawns` because the two mean opposite things: a spawn is stone you may
   * hit, a knock-out is stone that is already settled and merely still on screen.
   */
  debris: KnockOut[];
  /** Rubble that has landed and stays landed, newest last. Capped at `SETTLED_MAX`. */
  settled: SettledRock[];
  loot: LootDTO | null;
  /**
   * True while a broken rock is still in the air and the loot card is held back.
   *
   * The delay is on the *card*, never on the phase: `phase` becomes `'loot'` the instant
   * the server says broken, so swinging and stepping are locked out for the whole flight.
   * A phase that lagged the break would leave a window where the player could swing at a
   * rock that no longer exists.
   */
  lootPending: boolean;
  claim: ClaimDTO | null;
  blocker: Blocker | null;
  /** Client-side crack stage 0-5, corrected by every server response. */
  crackStage: number;
  lastCrit: boolean;
  lastHeavySwing: boolean;
  /** Set when the last swing was rejected for cadence, so the UI can go quiet. */
  throttled: boolean;
  /**
   * A transient line for the player when something went wrong that is not a
   * blocker — a lost run, a vanished rock, a server fault.
   *
   * This exists because every cave action is fired as `void store.action()`. An
   * unhandled rejection there is invisible except in the console, which is how
   * "the walk button does nothing" happened: the request failed, the promise
   * rejected, and the UI showed absolutely nothing.
   */
  notice: string | null;
  /** Active cave hazard encountered during walking (bats, falling rocks, gas, lava, etc.) */
  activeHazard: { key: string; name: string; message: string; id: number } | null;
  /** `Date.now()` of the last accepted step, for the client cadence floor. */
  lastStepAt: number;

  enter(caveKey?: string): Promise<void>;
  /** Choose the cave the next descent opens. Persists; does not enter anything. */
  chooseCave(caveKey: string, caveName: string): void;
  step(): Promise<void>;
  /** Search the wall to one side. Costs fuel, once per wall per run. */
  search(side: 'left' | 'right'): Promise<void>;
  dismissSearchEvent(): void;
  /** Walk back up the tunnel to rock left behind. Never mints depth. */
  stepBack(): Promise<void>;
  /** Make a rock from the field the one being worked. */
  focusRock(rock: RockDTO): void;
  swing(): Promise<void>;
  collect(impressionUuid?: string): Promise<void>;
  dismissLoot(): void;
  /**
   * A knocked-out rock has finished falling. Called by the layer that owns the clock, with
   * the point the arc actually ended at, so the rock does not jump on the frame it settles.
   *
   * `spin` is the tumble's final angle for the same reason `u`/`v` are the arc's final
   * position and not a re-derived one: `drawSettled` used to roll its own hash-based spin,
   * so a rock landed mid-tumble and then snapped to an unrelated angle on the exact frame it
   * stopped moving — the settle was smooth and the rotation was not.
   */
  settleDebris(uuid: string, u: number, v: number, spin: number): void;
  exit(): Promise<void>;
  /** Close the run summary. Until this is called the last haul is still on screen. */
  dismissSummary(): void;
  clearBlocker(): void;
  dismissNotice(): void;
  dismissHazard(): void;
  resume(): Promise<void>;

  /** Internal: turn any thrown error into recovered state. Never rethrows. */
  _recover(e: unknown, fallback: RunPhase): Promise<void>;
}

function applyRun(run: RunDTO): Partial<RunState> {
  return {
    depth: run.depth_m,
    torchFuel: Number(run.torch_fuel),
    ...(run.torch_fuel_max ? { torchFuelMax: Number(run.torch_fuel_max) } : {}),
    zone: run.zone,
    ...(run.run_id ? { runId: run.run_id } : {}),
  };
}

/**
 * Write a rock's progress back into the field.
 *
 * `spawns` is a snapshot from the last `/cave/step`, so a rock worked since then is
 * stale in it — and the field draws crack stages from that array. Without this, you
 * could hit a wall rock four times, close the focus view, and watch it sit there
 * undamaged until the next step refreshed the list.
 *
 * Returns the same array reference when nothing matched, so an unrelated swing does
 * not re-render twenty rocks.
 */
function withRockProgress(
  spawns: RockDTO[],
  uuid: string,
  patch: Partial<RockDTO>,
): RockDTO[] {
  let hit = false;
  const next = spawns.map((s) => {
    if (s.spawn_uuid !== uuid) return s;
    hit = true;
    return { ...s, ...patch };
  });
  return hit ? next : spawns;
}

/** A rock the axe cannot break puts the run in `blocked`, not `breaking`. */
const phaseForRock = (rock: RockDTO | null): RunPhase =>
  rock == null ? 'walking' : rock.locked ? 'blocked' : 'breaking';

export const useRun = create<RunState>((set, get) => ({
  phase: 'idle',
  runId: null,
  depth: 0,
  torchFuel: 100,
  torchFuelMax: 100,
  zone: { name: 'Abandoned Mine', tint: '#3A2A1E' },
  caveKey: readCave().key,
  caveName: readCave().name,
  searchEvent: null,
  searching: false,
  summary: null,
  rock: null,
  spawns: [],
  debris: [],
  settled: [],
  loot: null,
  lootPending: false,
  claim: null,
  blocker: null,
  crackStage: 0,
  lastCrit: false,
  lastHeavySwing: false,
  throttled: false,
  notice: null,
  activeHazard: null,
  lastStepAt: 0,
  dismissHazard: () => set({ activeHazard: null }),

  /**
   * Every cave error, funnelled through one place.
   *
   * The rule: a store action fired as `void action()` must never reject. Each
   * code either resyncs state, becomes a blocker the UI can render, or becomes a
   * notice the player can read. Nothing escapes silently.
   */
  async _recover(e, fallback) {
    if (!(e instanceof ApiError)) {
      set({ phase: fallback, notice: 'Something broke. Try again.' });
      return;
    }

    switch (e.code) {
      /* Faster than the axe or the legs allow. Go quiet — the player cannot act
         on it and a scary message would imply they did something wrong. */
      case 'CHEAT_SUSPECTED':
      case 'RATE_LIMITED':
        set({ phase: fallback, throttled: true });
        return;

      /* The run is gone: a server restart, a session expiry, or the cave was
         exited in another tab. Return to the surface honestly. */
      case 'NO_ACTIVE_RUN':
        cancelLootTimer();
        set({
          phase: 'idle', rock: null, loot: null, claim: null, blocker: null,
          crackStage: 0, runId: null, depth: 0, debris: [], settled: [], lootPending: false,
          notice: 'Your descent ended. Enter the cave again.',
        });
        return;

      /* This rock is not ours or no longer exists — drop it and ask the server
         what is actually in front of us. */
      case 'ROCK_NOT_FOUND':
        set({ rock: null, crackStage: 0, phase: 'walking' });
        await get().resume().catch(() => undefined);
        return;

      /* Either already banked, or a rock blocks the path and must be broken
         first. Both mean the client's picture is stale: resync. */
      case 'ROCK_ALREADY_CLAIMED':
        set({ crackStage: 0 });
        await get().resume().catch(() => undefined);
        return;

      case 'UNAUTHENTICATED':
      case 'BANNED':
      case 'MAINTENANCE':
        set({ phase: 'idle', rock: null, notice: e.message });
        return;

      default:
        if (e.isActionable) {
          set({
            phase: fallback,
            blocker: { code: e.code as Blocker['code'], message: e.message, details: e.details },
          });
          return;
        }
        set({ phase: fallback, notice: e.message });
    }
  },

  async enter(caveKey = get().caveKey) {
    try {
      const run = await cave.enter(caveKey);
      cancelLootTimer();
      /* The server's own name for the cave it just opened, so a stale label from disk
         is corrected by the act of walking in. */
      const caveName = run.zone?.cave_name ?? get().caveName;
      writeCave(caveKey, caveName);
      set({
        lastStepAt: 0,
        caveKey,
        caveName,
        ...applyRun(run),
        phase: 'walking',
        searchEvent: null,
        searching: false,
        /* The server generates the field on entry, so the corridor is stone from
           the first frame rather than from the first step. */
        spawns: run.spawns ?? [],
        rock: null, loot: null, claim: null, blocker: null, crackStage: 0,
        notice: null, throttled: false, debris: [], settled: [], lootPending: false,
      });
    } catch (e) {
      /* A cave the player cannot open must not leave the chosen cave pointing at it,
         or every future DIG re-runs the same refusal. Fall back to the starter cave,
         which is the one cave nobody can be locked out of. */
      if (e instanceof ApiError && e.code === 'CAVE_LOCKED') {
        set({ caveKey: STARTER_CAVE, caveName: STARTER_CAVE_NAME });
        writeCave(STARTER_CAVE, STARTER_CAVE_NAME);
      }
      await get()._recover(e, 'idle');
    }
  },

  chooseCave(caveKey, caveName) {
    writeCave(caveKey, caveName);
    set({ caveKey, caveName });
  },

  async step() {
    const { phase, rock } = get();
    /* A rock the axe CAN break must be broken first — the server refuses the
       step anyway, and not sending it keeps the UI honest.
    
       A rock it cannot break is different: walking past is the intended escape,
       and the server expires the blocker to allow exactly that. Refusing to
       send the step here stranded the player at the first Granite with no way
       forward and no way to know why. `blocked` is therefore walkable. */
    if (phase === 'breaking' || phase === 'stepping') return;
    if (phase === 'blocked' && rock && !rock.locked) return;
    if (phase === 'idle' || phase === 'loot' || phase === 'adPending') return;

    /* Client-side cadence floor — see STEP_MIN_MS. */
    const now = Date.now();
    if (now - get().lastStepAt < STEP_MIN_MS) return;
    set({ lastStepAt: now });

    set({ phase: 'stepping' });
    try {
      const res = await cave.step();
      const rock = res.obstacle.type === 'rock' ? res.obstacle.rock ?? null : null;
      useSession.getState().applyEnergy(res.energy);
      const isHazard = res.event?.type === 'hazard';
      const hazardData = isHazard ? {
        key: res.event.key ?? 'falling',
        name: res.event.name ?? 'Cave Hazard',
        message: res.event.message ?? 'Watch your step!',
        id: Date.now(),
      } : get().activeHazard;

      set({
        ...applyRun(res),
        rock,
        spawns: res.spawns ?? [],
        crackStage: 0,
        phase: phaseForRock(rock),
        blocker: null,
        throttled: false,
        ...(isHazard ? { activeHazard: hazardData } : {}),
      });
    } catch (e) {
      await get()._recover(e, 'walking');
    }
  },

  async stepBack() {
    /* Same guards as `step`, minus the blocker rules: a rock in front of you is
       never a reason you cannot turn around. That is the whole point — the escape
       from a vein your axe cannot touch used to be ending the run. */
    const { phase } = get();
    if (phase === 'breaking' || phase === 'stepping') return;
    if (phase === 'idle' || phase === 'loot' || phase === 'adPending') return;
    if (get().depth <= 0) return;

    const now = Date.now();
    if (now - get().lastStepAt < STEP_MIN_MS) return;
    set({ lastStepAt: now });

    set({ phase: 'stepping' });
    try {
      const res = await cave.stepBack();
      useSession.getState().applyEnergy(res.energy);
      /* Walking back never spawns, and never clears a rock that is still standing
         there, so `rock` is left alone — only the field and the depth move. */
      set({
        ...applyRun(res),
        spawns: res.spawns ?? [],
        phase: phaseForRock(get().rock),
        throttled: false,
      });
    } catch (e) {
      await get()._recover(e, 'walking');
    }
  },

  /**
   * Point the swing at a different rock.
   *
   * `swing()` and `collect()` both key off `rock.spawn_uuid`, and the server will
   * accept a hit on any spawn that is still standing — so choosing a rock is
   * genuinely just this. Nothing about the payout moves client-side: the rock was
   * rolled, priced and gated at spawn time, and all this does is decide which of
   * the server's rocks the player is looking at.
   *
   * Refuses while a break is mid-flight, so a stray tap on the wall cannot redirect
   * a swing that has already been sent for the rock in front of you.
   */
  focusRock(rock) {
    const { phase } = get();
    if (phase === 'loot' || phase === 'adPending' || phase === 'stepping') return;
    if (swingInFlight) return;
    if (rock.spawn_uuid === get().rock?.spawn_uuid) return;

    set({
      rock,
      crackStage: rock.required_hits
        ? Math.min(4, Math.ceil((rock.hits_registered / rock.required_hits) * 4))
        : 0,
      loot: null,
      phase: phaseForRock(rock),
      throttled: false,
    });
  },

  async swing() {
    const { rock, phase } = get();
    if (!rock || phase !== 'breaking') return;

    /*
     * One swing on the wire at a time.
     *
     * `phase` does not become 'loot' until the response lands, so it is useless
     * as a guard: a player tapping fast fires N parallel /cave/hit calls that all
     * pass the check above. The server serialises them on a row lock, the first
     * one breaks the rock, and the rest arrived to find it already BROKEN — which
     * is where the 409s came from.
     *
     * The server is now idempotent about that, but idempotent is the safety net,
     * not the fix: the extra calls still cost a round trip each, still run the
     * cadence check while the rock is unbroken (which bumps `risk_score`, so
     * ordinary fast tapping reads as cheating), and can land out of order and
     * walk `crackStage` backwards or reopen a dismissed loot card.
     */
    if (swingInFlight) return;
    swingInFlight = true;

    /* Optimistic crack: the swing must feel instant. The server's crack_stage
       overwrites this a moment later. */
    const predicted = rock.required_hits
      ? Math.min(4, Math.ceil(((rock.hits_registered + 1) / rock.required_hits) * 4))
      : get().crackStage;
    set((s) => ({
      crackStage: predicted,
      throttled: false,
      /* Optimistic on the field as well as on the focused rock — the two are the
         same rock seen from two places and must not disagree for a round trip. */
      spawns: rock.required_hits
        ? withRockProgress(s.spawns, rock.spawn_uuid, {
            hits_registered: rock.hits_registered + 1,
          })
        : s.spawns,
    }));

    try {
      const res: HitDTO = await cave.hit(rock.spawn_uuid);
      set((s) => ({
        crackStage: res.crack_stage,
        lastCrit: res.crit,
        lastHeavySwing: res.heavy_swing ?? false,
        rock: s.rock
          ? { ...s.rock, hits_registered: res.hits_registered, required_hits: res.required_hits }
          : null,
        /* The corridor draws damage from `spawns`, so the damage has to land there
           too — otherwise a rock worked to its last hit looks untouched the moment
           the focus view closes. */
        spawns: withRockProgress(s.spawns, rock.spawn_uuid, {
          hits_registered: res.hits_registered,
          required_hits: res.required_hits,
        }),
      }));

      if (res.broken && res.loot) {
        /*
         * The rock leaves the wall and falls, and the card waits for it to land.
         *
         * Broken rock leaves the field first — the server has already moved it out of
         * `visible_spawns`, and leaving it drawn would offer a tap that 409s. It moves to
         * `debris`, which nothing can tap, so for the length of the fall the same stone is
         * on screen without being minable. That is the whole trick: the rock the player
         * just spent five swings on does not blink out of existence, it comes out of the
         * wall the way it would if the wall were real.
         *
         * `phase` goes to `'loot'` immediately even though the card is held back, because
         * the phase is what locks out swinging and stepping. Delaying it would open a
         * window in which the player could swing at a rock that no longer exists.
         */
        const { slot, u, v } = placementOf(rock);
        const knocked: KnockOut = {
          uuid: rock.spawn_uuid,
          slot,
          u,
          v,
          depthM: rock.depth_m ?? get().depth,
          materialKey: rock.material?.key ?? 'loose',
          startedAt: performance.now(),
        };

        set((s) => ({
          phase: 'loot',
          loot: res.loot ?? null,
          crackStage: 5,
          spawns: s.spawns.filter((sp) => sp.spawn_uuid !== rock.spawn_uuid),
          debris: [...s.debris, knocked],
          lootPending: true,
        }));

        /* One timer, cleared and replaced. Two rocks cannot break at once — the swing
           guard sees to that — but a stale timer from an abandoned run could otherwise
           reveal the next run's card early. */
        if (lootTimer !== null) clearTimeout(lootTimer);
        lootTimer = setTimeout(() => {
          lootTimer = null;
          set({ lootPending: false });
        }, LOOT_CARD_DELAY_MS);
      }
    } catch (e) {
      if (e instanceof ApiError && (e.code === 'CHEAT_SUSPECTED' || e.code === 'RATE_LIMITED')) {
        /* Swung faster than the axe allows. Roll the optimistic crack back to
           the server's last known count and go quiet — the player cannot act on
           this and it is not their mistake. */
        set((s) => ({
          throttled: true,
          crackStage: s.rock?.required_hits
            ? Math.min(4, Math.ceil((s.rock.hits_registered / s.rock.required_hits) * 4))
            : 0,
          /* Roll the field's optimistic hit back too. A rejected swing that left the
             wall looking one hit further along would be the client inventing damage. */
          spawns: withRockProgress(s.spawns, rock.spawn_uuid, {
            hits_registered: rock.hits_registered,
          }),
        }));
        return;
      }
      await get()._recover(e, 'breaking');
    } finally {
      /* finally, not after the try: a thrown error must release the guard or the
         rock becomes unbreakable for the rest of the run. */
      swingInFlight = false;
    }
  },

  /**
   * Bank the loot. `impressionUuid` is passed only when a rewarded ad verified —
   * without it the server pays the coins and forfeits the Kyat, which is the
   * whole rule made concrete.
   */
  async collect(impressionUuid) {
    const { rock } = get();
    if (!rock) return;

    set({ phase: 'adPending' });
    try {
      const res = await cave.claim(rock.spawn_uuid, impressionUuid);
      const session = useSession.getState();
      session.applyWallet(res.wallet);
      session.addXp(res.profile.level, res.profile.xp, res.profile.xp_to_next);
      set({
        claim: res,
        phase: 'walking',
        rock: null,
        crackStage: 0,
        /* Rocky's auto-collect skips the card entirely — but only ever when no
           Kyat was on the line (enforced server-side). */
        loot: res.auto_collected ? null : get().loot,
      });
    } catch (e) {
      /* Keep the loot card open — the reward is still unbanked, and closing it
         would look like the player lost the drop. */
      set({ phase: 'loot' });
      await get()._recover(e, 'loot');
    }
  },

  dismissLoot() {
    set({ loot: null, claim: null, lootPending: false });
    cancelLootTimer();
  },

  settleDebris(uuid, u, v, spin) {
    set((s) => {
      const done = s.debris.find((d) => d.uuid === uuid);
      if (!done) return {};
      const rock: SettledRock = {
        uuid, u, v, depthM: done.depthM, materialKey: done.materialKey, spin,
      };
      return {
        debris: s.debris.filter((d) => d.uuid !== uuid),
        /* Oldest out first. `slice` rather than `shift` because this is state. */
        settled: [...s.settled, rock].slice(-SETTLED_MAX),
      };
    });
  },

  /**
   * Search the wall beside you.
   *
   * The only route to a hidden chest, a mystery door, or a layer of the map to the
   * next cave — and until now the only thing in the game with an API client method
   * and no caller anywhere. The endpoint has existed since the run engine was
   * written; nothing in the UI ever asked it a question.
   *
   * Guarded against a second in-flight search rather than a second search per wall:
   * the server owns the once-per-wall rule (`searched_walls`), and duplicating it
   * here would mean two opinions about which walls are spent, with the client's one
   * lost on every refresh.
   */
  async search(side) {
    const { phase, searching } = get();
    if (searching) return;
    /* Mid-transaction only. A rock in the path (`breaking`) is not a reason you cannot
       turn and look at the wall — and it is where most of a descent is spent, so
       excluding it made the mechanic look broken more often than available. */
    if (phase === 'idle' || phase === 'loot' || phase === 'adPending' || phase === 'stepping') {
      return;
    }

    set({ searching: true });
    try {
      const res = await cave.search(side);
      set({ ...applyRun(res), searchEvent: res.event, searching: false });
      /* A finished map changes what the map screen may offer, and the player is
         standing in a cave when it happens — so the list has to be refetched rather
         than patched from here. The store that owns it decides how. */
      if (res.event?.opened) window.dispatchEvent(new CustomEvent('shwetap:cave-opened'));
    } catch (e) {
      set({ searching: false });
      await get()._recover(e, 'walking');
    }
  },

  dismissSearchEvent() {
    set({ searchEvent: null });
  },

  dismissNotice() {
    set({ notice: null });
  },

  async exit() {
    let summary: RunSummaryDTO | null = null;
    try {
      summary = await cave.exit();
    } catch {
      /* The run is over either way — the player asked to leave and the cave is coming
         down. A failed exit costs them the summary, not the ability to surface. */
    } finally {
      cancelLootTimer();
      set({
        phase: 'idle', depth: 0, rock: null, loot: null, claim: null,
        blocker: null, crackStage: 0, runId: null, debris: [], settled: [], lootPending: false,
        searchEvent: null, searching: false,
        /* Kept across the reset — everything else here is being torn down, and this is
           the one piece of the run that is supposed to outlive it. */
        summary,
      });
      void useSession.getState().refresh();
    }
  },

  dismissSummary() {
    set({ summary: null });
  },

  clearBlocker() {
    set({ blocker: null });
  },

  /** Recover an interrupted descent after a refresh or app kill. */
  async resume() {
    try {
      const state = await cave.state();
      if (!state.active) {
        cancelLootTimer();
        set({ phase: 'idle', rock: null, crackStage: 0, runId: null, debris: [], settled: [], lootPending: false });
        return;
      }
      const rock = (state.active_rock ?? null) as RockDTO | null;
      set({
        depth: state.depth_m ?? 0,
        torchFuel: Number(state.torch_fuel ?? 100),
        zone: state.zone ?? { name: 'Abandoned Mine', tint: '#3A2A1E' },
        rock,
        /* A resumed run comes back with its corridor, not just its rock — the
           field is the cave, and rebuilding it from one rock is not possible. */
        spawns: state.spawns ?? [],
        phase: phaseForRock(rock),
      });
    } catch (e) {
      /* Resume runs on mount, before the player has done anything. A failure
         here must not throw into Shell's effect — land at the surface. */
      if (e instanceof ApiError) set({ phase: 'idle', notice: e.message });
      else set({ phase: 'idle' });
    }
  },
}));

export const selectTorchPct = (s: RunState): number =>
  s.torchFuelMax > 0 ? Math.max(0, Math.min(100, (s.torchFuel / s.torchFuelMax) * 100)) : 0;

export const selectInCave = (s: RunState): boolean => s.phase !== 'idle';
